// ============================================================================
// PRELIT-COMM-002 — Resolve Commitment
// ============================================================================
// Medium modal. 4 outcomes: HONOURED / PARTIAL / BROKEN / CANCELLED.
// HONOURED -> mark_recovered trigger (CLOSED_RECOVERED + closure reason)
// PARTIAL  -> stays in COMMITMENT (no trigger, no close)
// BROKEN   -> mark_broken trigger (-> BROKEN state)
// CANCELLED -> no trigger; agent can capture a new commitment

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useResolveCommitment } from 'hooks/api/usePreLitigation';
import { useClosureReasons } from 'hooks/api/useClosureReasons';
import type { CommitmentOutcome, Commitment } from 'api/types/preLitigation.types';

const OUTCOME_OPTIONS: { value: CommitmentOutcome; label: string }[] = [
  { value: 'HONOURED',  label: 'Honoured (Full Recovery)' },
  { value: 'PARTIAL',   label: 'Partial Recovery' },
  { value: 'BROKEN',    label: 'Broken' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

interface FormState {
  outcome: CommitmentOutcome;
  honouredAmount: string;
  honouredAt: string;
  closureReasonId: string;
  notes: string;
}

interface ResolveCommitmentModalProps {
  isOpen: boolean;
  commitment: Commitment | null;
  caseCode: string;
  onClose: () => void;
  onResolved: () => void;
}

const buildInitial = (commitment: Commitment | null): FormState => ({
  outcome: 'HONOURED',
  honouredAmount: commitment ? String(commitment.committedAmount) : '',
  honouredAt: nowLocalISO(),
  closureReasonId: '',
  notes: '',
});

function nowLocalISO(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

const ResolveCommitmentModal: React.FC<ResolveCommitmentModalProps> = ({
  isOpen, commitment, caseCode, onClose, onResolved,
}) => {
  const [form, setForm] = useState<FormState>(buildInitial(null));
  const snapRef = useRef('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const resolve = useResolveCommitment();

  // Closure reasons — filter by outcome category in the dropdown
  const { data: closureReasons } = useClosureReasons({ page: 1, pageSize: 200 });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const next = buildInitial(commitment);
    setForm(next);
    snapRef.current = JSON.stringify(next);
    setFieldErrors({});
  }, [isOpen, commitment?.id]);

  // When outcome flips to HONOURED auto-fill amount; when PARTIAL leave editable;
  // when BROKEN or CANCELLED clear honoured fields.
  useEffect(() => {
    if (!commitment) return;
    setForm(prev => {
      if (prev.outcome === 'HONOURED') {
        return { ...prev, honouredAmount: String(commitment.committedAmount) };
      }
      if (prev.outcome === 'BROKEN' || prev.outcome === 'CANCELLED') {
        return { ...prev, honouredAmount: '', honouredAt: '', closureReasonId: '' };
      }
      return prev;
    });
  }, [form.outcome, commitment]);

  const isDirty = useMemo(() => snapRef.current && JSON.stringify(form) !== snapRef.current, [form]);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFieldErrors(p => { const n = { ...p }; delete n[field as string]; return n; });
    setForm(p => ({ ...p, [field]: value }));
  };

  const closureReasonOptions = useMemo(() => {
    const filterCat =
      form.outcome === 'HONOURED' ? 'FAVOURABLE' :
      form.outcome === 'BROKEN'   ? 'UNFAVOURABLE' :
      null;
    if (!filterCat) return [];
    return (closureReasons ?? [])
      .filter((r: any) => r.outcomeCategory === filterCat)
      .map((r: any) => ({ value: String(r.id), label: r.closureReasonName ?? r.closureCode ?? String(r.id) }));
  }, [form.outcome, closureReasons]);

  const handleClose = useCallback(() => {
    if (resolve.loading) return;
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, resolve.loading, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (form.outcome === 'HONOURED' || form.outcome === 'PARTIAL') {
      const amt = parseFloat(form.honouredAmount);
      if (!form.honouredAmount || Number.isNaN(amt) || amt < 0) errors.honouredAmount = 'Enter a non-negative amount.';
      if (!form.honouredAt) errors.honouredAt = 'Honoured At is required.';
      if (form.outcome === 'HONOURED' && !form.closureReasonId) errors.closureReasonId = 'Pick a closure reason.';
    }
    if (form.outcome === 'BROKEN' && !form.closureReasonId) errors.closureReasonId = 'Pick a closure reason.';
    if (form.notes.length > 2000) errors.notes = 'Max 2000 chars';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form]);

  const handleConfirmSave = useCallback(async () => {
    if (!commitment) return;
    setSaveConfirmOpen(false);
    try {
      await resolve.run(commitment.id, {
        outcome: form.outcome,
        honouredAmount: form.outcome === 'HONOURED' || form.outcome === 'PARTIAL'
          ? parseFloat(form.honouredAmount) : undefined,
        honouredAt: form.outcome === 'HONOURED' || form.outcome === 'PARTIAL'
          ? new Date(form.honouredAt).toISOString() : undefined,
        closureReasonId: form.outcome === 'HONOURED' || form.outcome === 'BROKEN'
          ? form.closureReasonId : undefined,
        notes: form.notes || undefined,
      });
      const msg =
        form.outcome === 'HONOURED'  ? `Commitment honoured. Case ${caseCode} closed (recovered).` :
        form.outcome === 'PARTIAL'   ? `Partial recovery recorded on ${caseCode}.` :
        form.outcome === 'BROKEN'    ? `Commitment marked broken on ${caseCode}.` :
        `Commitment cancelled on ${caseCode}.`;
      notificationService.toast.success(msg);
      onResolved();
      onClose();
    } catch (err: any) {
      const m = err?.response?.data?.message ?? 'Failed to resolve commitment.';
      notificationService.toast.error(m, 6000);
    }
  }, [resolve, commitment, form, caseCode, onResolved, onClose]);

  if (!isOpen || !commitment) return null;

  const showHonouredFields = form.outcome === 'HONOURED' || form.outcome === 'PARTIAL';
  const showClosureReason = form.outcome === 'HONOURED' || form.outcome === 'BROKEN';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {resolve.loading && <LoadingOverlay text="Resolving commitment..." />}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Resolve Commitment — {caseCode}</h2>
          <button className="modal-close" onClick={handleClose} disabled={resolve.loading}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{
            padding: '0.5rem 0.75rem', background: 'var(--bg-secondary)',
            borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem',
          }}>
            <strong>Promised:</strong> ₹{commitment.committedAmount.toLocaleString('en-IN')} due{' '}
            {new Date(commitment.committedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </div>

          <div className="form-group" data-field="outcome">
            <SyutSelect
              label={<>Outcome <span className="text-danger">*</span></>}
              options={OUTCOME_OPTIONS}
              value={form.outcome}
              onChange={(v) => setField('outcome', v as CommitmentOutcome)}
              isClearable={false}
              error={fieldErrors.outcome}
            />
          </div>

          {showHonouredFields && (
            <div className="form-grid-2">
              <div className="form-group" data-field="honouredAmount">
                <label className="form-label">
                  Honoured Amount ₹ <span className="text-danger">*</span>
                  {form.outcome === 'HONOURED' && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (auto-filled)</span>}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={`form-input${fieldErrors.honouredAmount ? ' form-field-error' : ''}`}
                  value={form.honouredAmount}
                  onChange={(e) => setField('honouredAmount', e.target.value)}
                  readOnly={form.outcome === 'HONOURED'}
                  style={form.outcome === 'HONOURED' ? { background: 'var(--bg-secondary)' } : undefined}
                />
                {fieldErrors.honouredAmount && <div className="form-error-message">{fieldErrors.honouredAmount}</div>}
              </div>
              <div className="form-group" data-field="honouredAt">
                <label className="form-label">Honoured At <span className="text-danger">*</span></label>
                <input
                  type="datetime-local"
                  className={`form-input${fieldErrors.honouredAt ? ' form-field-error' : ''}`}
                  value={form.honouredAt}
                  onChange={(e) => setField('honouredAt', e.target.value)}
                />
                {fieldErrors.honouredAt && <div className="form-error-message">{fieldErrors.honouredAt}</div>}
              </div>
            </div>
          )}

          {showClosureReason && (
            <div className="form-group" data-field="closureReasonId">
              <SyutSelect
                label={<>Closure Reason <span className="text-danger">*</span></>}
                options={closureReasonOptions}
                value={form.closureReasonId}
                onChange={(v) => setField('closureReasonId', (v as string) || '')}
                error={fieldErrors.closureReasonId}
                placeholder={form.outcome === 'HONOURED' ? 'Pick a favourable reason...' : 'Pick an unfavourable reason...'}
              />
            </div>
          )}

          <div className="form-group" data-field="notes">
            <label className="form-label">Notes <span style={{ color: 'var(--text-muted)' }}>(optional)</span></label>
            <textarea
              className={`form-textarea${fieldErrors.notes ? ' form-field-error' : ''}`}
              rows={3}
              maxLength={2000}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
            />
            {fieldErrors.notes && <div className="form-error-message">{fieldErrors.notes}</div>}
          </div>

          {form.outcome === 'PARTIAL' && (
            <div style={{
              padding: '0.5rem 0.75rem', background: '#fef3c7', color: '#92400e',
              borderRadius: '6px', fontSize: '0.8rem', border: '1px solid #f59e0b',
            }}>
              Partial recovery — case stays in COMMITMENT state. Capture a new commitment for the balance, or mark broken if no further commitment is given.
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={resolve.loading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={resolve.loading}>Resolve</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Resolution"
        message={`Resolve as ${form.outcome}?`}
        confirmLabel="Resolve"
        cancelLabel="Cancel"
        variant={form.outcome === 'HONOURED' ? 'success' : form.outcome === 'BROKEN' ? 'danger' : 'default'}
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

const LoadingOverlay: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
  }}>
    <div style={{
      width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
      borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{text}</span>
  </div>
);

export default ResolveCommitmentModal;
